import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import {pluralize} from 'BenchlingUtil';
import {gc, meltingTemperature} from 'biojs/Util';
import {PrimerPreferences} from 'constants/PrimerConstants';
import FileStates from 'FileStates';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import MeltingTempModal from 'molbio/melting-temp/MeltingTempModal';
import {
  RelevantItemsFilterOption,
  relevantItemsFilteredTableState,
} from 'relevant-items/PresentationalRelevantItemsSection';
import RelevantItemsTabState from 'relevant-items/RelevantItemsTabState';
import {
  FONT_PROXIMA_13,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
  FONT_WEIGHT_PROXIMA_REGULAR,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {MELTING_TEMP_ABBREVIATION, DEGREE_SIGN} from 'util/UnicodeSymbols';

import {OligoInfoQuery, OligoInfoQueryVariables} from './__generated/OligoInfoQuery';

export const OLIGO_INFO_QUERY = gql`
  query OligoInfoQuery($id: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    componentSequence(id: $id) @client {
      bases @client
      id @client
      nucleotideType @client
    }
    sequence(id: $id) {
      ... on SequenceNode {
        id
        relevantItems {
          sequencesUsingAsPrimer {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function OligoPropertyText({label, value}: {label: ReactNode; value: ReactNode}): JSX.Element {
  return (
    <>
      <span className={css(styles.labelText)}>{label}</span>&nbsp;
      <span className={css(styles.valueText)}>{value}</span>
    </>
  );
}

function Separator(): JSX.Element {
  return <div className={css(styles.separator)} />;
}

export default function OligoPropertiesBanner({sequenceId}: {sequenceId: string}): JSX.Element {
  const [_, setFilteredTableState] = relevantItemsFilteredTableState.useGlobalClientState(sequenceId);
  // `meltingTemperature` uses Tm preferences, so force a re-render if they change.
  usePreference(PrimerPreferences.MELTING_TEMP_DNA);
  usePreference(PrimerPreferences.MELTING_TEMP_RNA);

  return (
    <BenchlingQuery<OligoInfoQuery, OligoInfoQueryVariables>
      query={OLIGO_INFO_QUERY}
      variables={{id: sequenceId}}
      renderLoading={() => <div />}
      renderCachedDataWhenRefetching={true}
    >
      {(data: OligoInfoQuery) => {
        if (isInaccessible(data.sequence) || data.componentSequence == null) {
          return null;
        }
        const bases = data.componentSequence.bases;
        const nucleotideType = data.componentSequence.nucleotideType;
        const gcPercent = gc(bases, nucleotideType) * 100;
        const tm = meltingTemperature(bases, nucleotideType);
        const linkedSequenceCount = data.sequence.relevantItems.sequencesUsingAsPrimer.length;
        // Note: Accesses FileStates without listening to changes. This is acceptable because
        // relevantItemsBufferTab doesn't change after mount. If it did, we'd need to listen to FileStates.
        const sequenceFileState = FileStates.get().get(sequenceId);
        // RelevantItemsTab may be missing in certain cases:
        // - In share link mode (when getActiveShareLinkItemId() != null)
        // - When using DisplaySequenceFileState which overrides hasRelevantItemsTab() to return false
        // - In rare cases, unmounting PolymerLinearMapView, which renders this React component, can race the
        //   teardown of the SequenceFileState, in which case sequenceFilestate itself is null
        const relevantItemsBufferTab = sequenceFileState?.buffers().getTabByClass(RelevantItemsTabState);
        return (
          <div
            className={css(styles.banner)}
            {...getTestProps({component: 'OligoPropertiesBanner', element: 'root'})}
          >
            <div className={css(styles.buffer)} />
            <OligoPropertyText label="Length" value={bases.length} />
            <Separator />
            <OligoPropertyText label="GC" value={`${gcPercent.toFixed(2)}%`} />
            <Separator />
            <OligoPropertyText
              label={
                <>
                  {MELTING_TEMP_ABBREVIATION}
                  <BenchlingIcon
                    icon="settings"
                    onClick={() => {
                      legacyRunModal(MeltingTempModal, {});
                    }}
                    className={css(styles.tmSettings)}
                    {...getTestProps({
                      component: 'OligoPropertiesBanner',
                      element: 'tmSettings',
                    })}
                  />
                </>
              }
              value={`${tm.toFixed(1)}${DEGREE_SIGN}C`}
            />
            {linkedSequenceCount > 0 && relevantItemsBufferTab != null && (
              <>
                <Separator />
                <Button
                  className={css(styles.linkedSequences)}
                  buttonStyle={ButtonStyle.LINK}
                  onClick={() => {
                    setFilteredTableState({
                      searchQuery: '',
                      filterOption: RelevantItemsFilterOption.SEQUENCE_PRIMERS,
                      pageOffset: 0,
                    });
                    relevantItemsBufferTab.set({active: true});
                  }}
                >
                  {linkedSequenceCount} linked {pluralize(linkedSequenceCount, 'sequence')}
                </Button>
              </>
            )}
            <div className={css(styles.buffer)} />
          </div>
        );
      }}
    </BenchlingQuery>
  );
}
const styles = StyleSheet.create({
  banner: {
    ...FONT_PROXIMA_13,
    backgroundColor: GRAY_LIGHTER,
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
  },
  labelText: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  valueText: {
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  buffer: {
    flex: 1,
  },
  linkedSequences: {
    verticalAlign: 'text-top',
  },
  separator: {
    height: '24px',
    borderRight: `1px solid ${GRAY_DARKER}`,
    margin: '0px 10px',
  },
  tmSettings: {
    // Manually set width because our less code tries to set it to 100% :(
    width: '13px',
    marginBottom: '0px',
    color: GRAY_DARKER,
    cursor: 'pointer',
    margin: '0px 3px',
  },
});
